import apiClient from './base/apiClient'

export interface BookmarkCategory {
  id: string
  name: string
  user_id?: string // 소유자 ID
  thumbnail_image?: string | null // 썸네일 이미지 URL
  is_public?: boolean // 공개 여부 (true: 전체 공개, false: 비공개)
  is_member?: boolean // 멤버 여부
  /** true면 기본 플레이트리스트(목록·선택 드롭다운에서 숨김) */
  is_default?: boolean
  items: Array<{
    id: string
    name: string
    address: string
    isBookmarked: boolean
    /** 장소 카테고리 코드 (restaurant/cafe/bar) — 카테고리 필터용 */
    category?: string
    /** 현재 사용자의 리뷰 존재 여부 */
    has_review?: boolean
    /** 이 리스트에 이 장소를 넣은 사람의 id. 저장 시트가 "내 저장인지" 판단하는 데 쓴다. */
    added_by_user_id?: string | null
    /** 이 리스트에 담은 시각(ISO). "저장한 장소"를 담은 순으로 세우는 데 쓴다. */
    saved_at?: string | null
    /** 이 장소를 리스트에 추가한 사람 (플레이트리스트 아바타용). 없으면 null. */
    added_by?: {
      id: string
      nickname: string
      username?: string
      profile_image: string | null
    } | null
  }>
}

export interface GetBookmarksResponse {
  categories: BookmarkCategory[]
}

export interface BookmarkCategoryItem {
  id: number
  name: string
  thumbnail_image: string | null
  is_public?: boolean // 공개 여부 (false: 나만보기)
  created_at: string
}

export interface GetBookmarkCategoriesResponse {
  categories: BookmarkCategoryItem[]
}

export interface ToggleBookmarkRequest {
  bookmark_category_id?: string // 북마크 카테고리 ID (선택적, 없으면 기본 카테고리)
}

export interface ToggleBookmarkResponse {
  is_bookmarked: boolean
}

export interface CreateBookmarkCategoryRequest {
  name: string
  thumbnailImage?: File
  memberIds?: string[]
  isPublic?: boolean // 공개 여부 (true: 전체 공개, false: 비공개)
  placeIds?: number[] // 링크 가져오기로 미리 확보한 장소들
}

/** 가져오기 결과의 장소 요약. 목록을 그리는 데 필요한 최소 정보만 담긴다. */
export interface ImportedPlaceSummary {
  id: number
  name: string
  address: string | null
}

/**
 * 링크 안에서 고를 수 있는 묶음 (네이버 기획전의 지역 등).
 *
 * 기획전 하나에 수백 곳이 들어 있어 전부 담으면 부담이 크다. 서버가 묶음을
 * 돌려주면 화면이 고르게 하고, 고른 것만 다시 보낸다.
 */
export interface ImportGroup {
  id: string
  name: string
  count: number
  /**
   * 이 묶음에 속한 장소들.
   *
   * 서버가 이미 다 찾아서 담아 두고 묶음별로 나눠 보낸다. 그래서 화면은
   * 고른 묶음의 id 를 모으기만 하면 되고 **서버를 다시 부르지 않는다.**
   * (예전에는 고른 뒤 다시 불러서 수백 곳을 처음부터 또 찾았다)
   */
  place_ids: number[]
}

export interface ResolveSharedLinkResponse {
  folder_name: string | null
  total: number
  /**
   * true 면 아직 아무것도 안 담았다는 뜻이다. groups 를 보여주고
   * 고른 id 들을 groupIds 로 다시 보내야 한다.
   */
  needsGroupChoice?: boolean
  groups?: ImportGroup[]
  /**
   * 묶음의 종류. groups 가 있을 때만 온다 (옛 서버는 안 준다 → 'region' 으로 본다).
   *   region  주소로 나눈 지역 (네이버 기획전 등)
   *   list    한 사람의 네이버 공개 리스트들
   */
  groupKind?: 'region' | 'list'
  /** 네이버 공개 리스트 주인의 닉네임. 그 외 링크는 null 이거나 없다. */
  ownerName?: string | null
  imported: number
  place_ids: number[]
  /**
   * 가져온 장소의 이름·주소.
   * 이게 없던 시절에는 화면이 place_ids만 받아 장소마다 상세 API를 다시 불렀다.
   * 400곳이면 400번을 왕복해 토스트가 뜬 뒤에도 한참 스피너가 돌았다.
   */
  places: ImportedPlaceSummary[]
  skipped: Array<{ name: string; reason: string }>
}

export interface CreateBookmarkCategoryResponse {
  message: string
  category: {
    id: string
    name: string
    thumbnail_image: string | null
    created_at: string
  }
}

export interface InvitableUser {
  id: string
  nickname: string
  username: string
  profile_image: string | null
  is_member: boolean
}

export interface GetInvitableUsersResponse {
  users: InvitableUser[]
}

/** 인스타 공유 해석 결과의 장소 한 건 */
export interface InstagramPlace {
  kakao_place_id: string
  name: string
  address: string
  category: string
  lat: number | null
  lng: number | null
}

/** 캡션에서 뽑은 후보 하나의 처리 결과 */
export interface InstagramResult {
  /** 캡션에 적혀 있던 상호 */
  name: string
  /** 캡션에 같이 적힌 인스타 계정 (가장 강한 단서) */
  handle: string | null
  /**
   * confirmed  한 곳으로 확정됨
   * ambiguous  여러 곳이라 사용자가 골라야 함
   * not_dining 찾았지만 먹는 곳이 아님 (사진관 등) — 제외가 맞다
   * not_found  카카오에서 못 찾음
   */
  status: 'confirmed' | 'ambiguous' | 'not_dining' | 'not_found'
  /**
   * 무엇으로 확정했나.
   *   handle        캡션의 @계정 ↔ 카카오 홈페이지
   *   address       캡션에 적힌 주소 둘레
   *   region        '#서촌' 같은 동네 둘레
   *   broad_region  '부산' 같은 시·도 — 전국에서 찾은 뒤 주소로 걸렀다
   *   name          상호만으로
   */
  matched_by: string | null
  place: InstagramPlace | null
  candidates: InstagramPlace[]
}

export interface InstagramResolveResponse {
  post: { url: string; author: string | null; thumbnail: string | null }
  region: string | null
  results: InstagramResult[]
  summary: {
    confirmed: number
    ambiguous: number
    not_dining: number
    not_found: number
  }
}

export const bookmarkApi = {
  /** addedByTarget=true면 "저장한 장소" 화면용 — 그 사용자가 직접 추가한 장소만 내려온다
   *  (플레이트리스트에 다른 멤버가 넣은 장소는 제외). 저장 모달·필터 등에서는 넘기지 않는다. */
  getBookmarks: async (
    userId?: string,
    addedByTarget?: boolean
  ): Promise<GetBookmarksResponse> => {
    const base = userId ? `/bookmarks/user/${userId}` : '/bookmarks'
    const url = addedByTarget ? `${base}?added_by_target=1` : base
    const response = await apiClient.get<GetBookmarksResponse>(url)
    return response.data
  },
  getBookmarksByCategory: async (
    categoryId: string
  ): Promise<GetBookmarksResponse> => {
    const response = await apiClient.get<GetBookmarksResponse>(
      `/bookmarks/${categoryId}`
    )
    return response.data
  },
  toggleBookmark: async (
    placeId: number,
    data?: ToggleBookmarkRequest
  ): Promise<ToggleBookmarkResponse> => {
    const response = await apiClient.post<ToggleBookmarkResponse>(
      `/bookmarks/place/${placeId}`,
      data
    )
    return response.data
  },
  /**
   * "저장한 장소"에서 완전 해제 — 내가 추가(added_by=나)한 모든 카테고리에서 이 장소 제거.
   * (한 카테고리만 빼는 toggleBookmark와 달리, 다른 리스트에 남아 되살아나거나 카운트가 안 줄던 문제 해결)
   */
  removeSavedPlace: async (
    placeId: number
  ): Promise<{ is_bookmarked: boolean; removed: number }> => {
    const response = await apiClient.post<{
      is_bookmarked: boolean
      removed: number
    }>(`/bookmarks/place/${placeId}/unsave`)
    return response.data
  },
  getBookmarkCategories: async (
    userId?: string
  ): Promise<GetBookmarkCategoriesResponse> => {
    const url = userId
      ? `/bookmarks/categories/${userId}`
      : '/bookmarks/categories'
    const response = await apiClient.get<GetBookmarkCategoriesResponse>(url)
    return response.data
  },
  createBookmarkCategory: async (
    data: CreateBookmarkCategoryRequest
  ): Promise<CreateBookmarkCategoryResponse> => {
    const formData = new FormData()
    formData.append('name', data.name)
    if (data.thumbnailImage) {
      formData.append('thumbnail_image', data.thumbnailImage)
    }
    if (data.memberIds && data.memberIds.length > 0) {
      data.memberIds.forEach((memberId) => {
        formData.append('member_ids[]', memberId)
      })
    }
    if (data.isPublic !== undefined) {
      formData.append('is_public', data.isPublic.toString())
    }
    if (data.placeIds && data.placeIds.length > 0) {
      data.placeIds.forEach((placeId) => {
        formData.append('place_ids[]', placeId.toString())
      })
    }

    const response = await apiClient.post<CreateBookmarkCategoryResponse>(
      '/bookmarks/category',
      formData
    )
    return response.data
  },
  /**
   * 인스타그램 공유 링크를 해석만 한다(저장 X).
   *
   * 후보를 미리 등록하면 고르지도 않은 장소가 쌓이므로, 등록은 사용자가
   * 고른 뒤 saveInstagramPlaces 에서 한 번만 일어난다.
   */
  resolveInstagramShare: async (
    url: string
  ): Promise<InstagramResolveResponse> => {
    const response = await apiClient.post<InstagramResolveResponse>(
      '/bookmarks/instagram/resolve',
      { url },
      { timeout: 60000 }
    )
    return response.data
  },
  /** 고른 장소만 등록하고 플레이트리스트에 담는다. */
  saveInstagramPlaces: async (
    url: string,
    kakaoPlaceIds: string[],
    /**
     * 담을 곳. 안 주면 '저장한 장소' 에만 넣는다.
     *
     * 예전에는 무조건 새 플레이트리스트를 만들어서, 링크를 넣을 때마다
     * 리스트가 하나씩 늘었다.
     */
    dest?: {
      /** 여러 곳에 담을 수 있다. 담기 시트가 체크 방식이라 배열로 온다 */
      categoryIds?: string[]
      categoryId?: string | null
      createList?: boolean
      name?: string
    }
  ): Promise<{
    message: string
    category_id: string | null
    category_name: string | null
    /** 저장된 우리 쪽 장소 id. 담기 시트가 이걸로 리스트를 넣고 뺀다 */
    place_ids: string[]
    imported: number
  }> => {
    const response = await apiClient.post<{
      message: string
      category_id: string | null
      category_name: string | null
      place_ids: string[]
      imported: number
    }>(
      '/bookmarks/instagram/save',
      {
        url,
        kakao_place_ids: kakaoPlaceIds,
        name: dest?.name,
        category_id: dest?.categoryId ?? undefined,
        category_ids: dest?.categoryIds?.length ? dest.categoryIds : undefined,
        create_list: dest?.createList ?? undefined,
      },
      { timeout: 60000 }
    )
    return response.data
  },
  /**
   * 네이버/카카오 공유 리스트 링크를 해석만 한다(리스트 생성 X).
   * 폴더명 + 매칭된 place_id 목록 반환 → 추가 시트에서 이름칸 채우고 완료 시 생성.
   */
  /**
   * [silent] 실패해도 토스트를 띄우지 않는다.
   *
   * 화면이 실패 사유를 **직접 그리는 경우**에만 쓴다 (MapLinkShare). 안 그러면
   * 같은 문장이 화면 가운데와 토스트에 두 번 뜬다. 토스트가 유일한 안내인
   * 화면(가져오기 모달·온보딩)에서는 끄면 안 된다.
   */
  resolveSharedLink: async (
    url: string,
    groupIds?: string[],
    opts?: { silent?: boolean }
  ): Promise<ResolveSharedLinkResponse> => {
    const response = await apiClient.post<ResolveSharedLinkResponse>(
      '/bookmarks/resolve-shared-link',
      { url, groupIds },
      {
        timeout: 120000,
        ...(opts?.silent ? { skipErrorHandler: true } : {}),
      } as never
    )
    return response.data
  },
  /**
   * 플레이트리스트 없이 **기본 저장함('저장한 장소')에만** 담는다.
   *
   * 링크로 가져올 때마다 플레이트리스트를 새로 만들면 금방 지저분해진다.
   * 대부분은 그냥 저장만 하고 싶을 뿐이라 이 길을 둔다.
   */
  savePlaces: async (
    placeIds: number[]
  ): Promise<{ message: string; saved: number }> => {
    const response = await apiClient.post<{ message: string; saved: number }>(
      '/bookmarks/save-places',
      { place_ids: placeIds },
      { timeout: 60000 }
    )
    return response.data
  },
  /**
   * 기존 플레이트리스트에 장소들을 추가 (이미 있는 장소는 제외).
   * 수정 모드에서 링크로 가져온 장소를 완료 시 연결할 때 사용.
   */
  /** 플레이트리스트에서 장소 여러 개를 뺀다. addPlacesToCategory 의 반대 */
  removePlacesFromCategory: async (
    categoryId: string,
    placeIds: number[]
  ): Promise<{ message: string; removed: number }> => {
    const response = await apiClient.post<{ message: string; removed: number }>(
      `/bookmarks/category/${categoryId}/remove-places`,
      { place_ids: placeIds }
    )
    return response.data
  },
  addPlacesToCategory: async (
    categoryId: string,
    placeIds: number[]
  ): Promise<{ message: string; added: number; duplicates: number }> => {
    const response = await apiClient.post<{
      message: string
      added: number
      duplicates: number
    }>(`/bookmarks/category/${categoryId}/add-places`, {
      place_ids: placeIds,
    })
    return response.data
  },
  deleteBookmarkCategory: async (
    categoryId: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(
      `/bookmarks/category/${categoryId}`
    )
    return response.data
  },
  updateBookmarkCategoryName: async (
    categoryId: string,
    name: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.put<{ message: string }>(
      `/bookmarks/category/${categoryId}/name`,
      { name }
    )
    return response.data
  },
  getCategoryMembers: async (
    categoryId: string
  ): Promise<GetInvitableUsersResponse> => {
    const response = await apiClient.get<GetInvitableUsersResponse>(
      `/bookmarks/category/${categoryId}/members`
    )
    return response.data
  },
  getInvitableUsers: async (
    categoryId?: string,
    keyword?: string
  ): Promise<GetInvitableUsersResponse> => {
    const params = new URLSearchParams()
    if (keyword !== undefined && keyword.trim() !== '') {
      params.set('keyword', keyword.trim())
    }
    const qs = params.toString()
    const suffix = qs ? `?${qs}` : ''
    const url = categoryId
      ? `/bookmarks/invitable-users/${categoryId}${suffix}`
      : `/bookmarks/invitable-users${suffix}`
    const response = await apiClient.get<GetInvitableUsersResponse>(url)
    return response.data
  },
  updateBookmarkCategoryMembers: async (
    categoryId: string,
    memberIds: string[]
  ): Promise<{ message: string }> => {
    const formData = new FormData()
    if (memberIds && memberIds.length > 0) {
      memberIds.forEach((memberId) => {
        formData.append('member_ids[]', memberId)
      })
    }

    const response = await apiClient.put<{ message: string }>(
      `/bookmarks/category/${categoryId}/members`,
      formData
    )
    return response.data
  },
  updateBookmarkCategoryPrivacy: async (
    categoryId: string,
    isPublic: boolean
  ): Promise<{ message: string }> => {
    const formData = new FormData()
    formData.append('is_public', isPublic.toString())

    const response = await apiClient.put<{ message: string }>(
      `/bookmarks/category/${categoryId}/privacy`,
      formData
    )
    return response.data
  },
  removeMemberFromCategory: async (
    categoryId: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(
      `/bookmarks/category/${categoryId}/member`
    )
    return response.data
  },
  updateBookmarkCategoryCoverImage: async (
    categoryId: string,
    thumbnailImage: File
  ): Promise<{ message: string }> => {
    const formData = new FormData()
    formData.append('thumbnail_image', thumbnailImage)

    const response = await apiClient.put<{ message: string }>(
      `/bookmarks/category/${categoryId}/cover-image`,
      formData
    )
    return response.data
  },
}
